import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Container,
  Grid,
  InputAdornment,
  TextField,
  Typography,
  CircularProgress,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { Link as RouterLink } from "react-router-dom";
import ApiIcon from "@mui/icons-material/Api";
import QuestionAnswerIcon from "@mui/icons-material/QuestionAnswer";
import LinkIcon from "@mui/icons-material/Link";
import DescriptionIcon from "@mui/icons-material/Description";
import CategoryIcon from "@mui/icons-material/Category";
import InventoryIcon from "@mui/icons-material/Inventory";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import RouterIcon from "@mui/icons-material/Router";
import ManageSearchIcon from "@mui/icons-material/ManageSearch";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import GroupIcon from "@mui/icons-material/Group";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import AnalyticsIcon from "@mui/icons-material/Analytics";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import PageTitle from "../../components/common/PageTitle";
import { PM_HUBS } from "../../utils/pmHubRoutes";
import { apiService } from "../../api/services/apiService";

const features = [
  {
    title: "API Hub",
    description: "Create, bulk upload, update, and enable or disable API specifications.",
    path: PM_HUBS.api,
    icon: <ApiIcon sx={{ fontSize: 40, color: "primary.main" }} />,
    countKey: "apis" as const,
  },
  {
    title: "FAQ Hub",
    description: "Manage frequently asked questions for your products.",
    path: PM_HUBS.faq,
    icon: <QuestionAnswerIcon sx={{ fontSize: 40, color: "primary.main" }} />,
    countKey: "faqs" as const,
  },
  {
    title: "Link Hub",
    description: "Manage external and reference links.",
    path: PM_HUBS.link,
    icon: <LinkIcon sx={{ fontSize: 40, color: "primary.main" }} />,
    countKey: "links" as const,
  },
  {
    title: "Document Hub",
    description: "Manage product documentation assets.",
    path: PM_HUBS.document,
    icon: <DescriptionIcon sx={{ fontSize: 40, color: "primary.main" }} />,
    countKey: "documents" as const,
  },
  {
    title: "Knowledge Search",
    description: "Search SOPs, runbooks, and other indexed library documents for this tenant.",
    path: PM_HUBS.knowledgeSearch,
    icon: <ManageSearchIcon sx={{ fontSize: 40, color: "primary.main" }} />,
    countKey: null,
  },
  {
    title: "Category Hub",
    description: "Organize products with categories.",
    path: PM_HUBS.category,
    icon: <CategoryIcon sx={{ fontSize: 40, color: "primary.main" }} />,
    countKey: "categories" as const,
  },
  {
    title: "Products",
    description: "Create and manage products and their associations.",
    path: PM_HUBS.product,
    icon: <InventoryIcon sx={{ fontSize: 40, color: "primary.main" }} />,
    countKey: "products" as const,
  },
  {
    title: "Management Groups",
    description: "Create groups and control who can modify assigned catalog assets.",
    path: PM_HUBS.productManagementGroup,
    icon: <GroupIcon sx={{ fontSize: 40, color: "primary.main" }} />,
    countKey: null,
  },
  {
    title: "Developer Guides",
    description: "Author API integration guides with sections and code samples.",
    path: PM_HUBS.developerGuide,
    icon: <MenuBookIcon sx={{ fontSize: 40, color: "primary.main" }} />,
    countKey: "developerGuides" as const,
  },
  {
    title: "Operational Errors",
    description: "Curate error codes, messages, and strings for production support operators.",
    path: PM_HUBS.operationalError,
    icon: <ErrorOutlineIcon sx={{ fontSize: 40, color: "primary.main" }} />,
    countKey: "operationalErrors" as const,
  },
  {
    title: "App KYC Hub",
    description: "Register and manage app keys, gateway details, and KYC contacts.",
    path: PM_HUBS.appKyc,
    icon: <VpnKeyIcon sx={{ fontSize: 40, color: "primary.main" }} />,
    countKey: "appKycs" as const,
  },
  {
    title: "Gateway SKU Hub",
    description: "Create, bulk upload, and manage gateway SKUs; link them to products for subscription availability.",
    path: PM_HUBS.gatewaySku,
    icon: <RouterIcon sx={{ fontSize: 40, color: "primary.main" }} />,
    countKey: "gatewaySkus" as const,
  },
  {
    title: "Insights",
    description: "View analytics and usage insights for your tenant.",
    path: "/analytics-dashboard",
    icon: <AnalyticsIcon sx={{ fontSize: 40, color: "primary.main" }} />,
  },
  {
    title: "Portfolio Intelligence",
    description:
      "Analyze API duplication risk, reuse rate, and governance coverage across your catalog.",
    path: PM_HUBS.apiPortfolioIntelligence,
    icon: <AssessmentOutlinedIcon sx={{ fontSize: 40, color: "primary.main" }} />,
  },
];

const ProductManagerDashboardPage: React.FC = () => {
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  const filteredFeatures = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return features;
    return features.filter(
      (feature) =>
        feature.title.toLowerCase().includes(query) ||
        feature.description.toLowerCase().includes(query),
    );
  }, [searchQuery]);

  useEffect(() => {
    const load = async () => {
      try {
        const [
          apis,
          faqs,
          links,
          documents,
          categories,
          products,
          appKycs,
          gatewaySkus,
          developerGuides,
          operationalErrors,
        ] = await Promise.all([
          apiService.getApis(),
          apiService.getFaqs(),
          apiService.getLinks(),
          apiService.getDocuments(),
          apiService.getCategories(),
          apiService.getProducts(),
          apiService.getAppKycs(),
          apiService.getGatewaySkus(),
          apiService.getDeveloperGuides(),
          apiService.getOperationalErrors(),
        ]);
        setCounts({
          apis: apis.length,
          faqs: faqs.length,
          links: links.length,
          documents: documents.length,
          categories: categories.length,
          products: products.length,
          appKycs: appKycs.length,
          gatewaySkus: gatewaySkus.length,
          developerGuides: developerGuides.length,
          operationalErrors: operationalErrors.length,
        });
      } catch {
        /* counts optional */
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
      <PageTitle
        pageTitle="Product Manager Dashboard"
        pageDescription="Manage APIs, FAQs, links, documents, categories, and products from dedicated feature hubs."
      />

      <Container maxWidth="lg" sx={{ py: 4 }}>
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
            <CircularProgress size={28} />
          </Box>
        )}

        <TextField
          fullWidth
          size="small"
          placeholder="Search hubs..."
          variant="outlined"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{
            mb: 3,
            maxWidth: 480,
            "& .MuiOutlinedInput-root": { borderRadius: 2 },
          }}
        />

        <Grid container spacing={3}>
          {filteredFeatures.map((feature) => {
            const countKey =
              "countKey" in feature ? feature.countKey : undefined;
            const itemCount =
              !loading && countKey != null ? counts[countKey] : undefined;

            return (
            <Grid item xs={12} sm={6} md={4} lg={3} key={feature.path}>
              <Card
                variant="outlined"
                sx={{
                  height: "100%",
                  borderRadius: 2,
                  transition: "box-shadow 0.2s",
                  "&:hover": { boxShadow: 4 },
                }}
              >
                <CardActionArea
                  component={RouterLink}
                  to={feature.path}
                  sx={{ height: "100%", alignItems: "stretch" }}
                >
                  <CardContent sx={{ textAlign: "center", py: 3 }}>
                    {feature.icon}
                    <Typography variant="h6" fontWeight={600} sx={{ mt: 2 }}>
                      {feature.title}
                    </Typography>
                    {itemCount != null && (
                      <Typography variant="caption" color="primary" display="block">
                        {itemCount} items
                      </Typography>
                    )}
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 1 }}
                    >
                      {feature.description}
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
            );
          })}
          {!loading && filteredFeatures.length === 0 && (
            <Grid item xs={12}>
              <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
                No hubs match &quot;{searchQuery.trim()}&quot;.
              </Typography>
            </Grid>
          )}
        </Grid>
      </Container>
    </Box>
  );
};

export default ProductManagerDashboardPage;
